// frontend/src/components/briefing/briefing-copy.ts
//
// The words of the Command Briefing that are not data: the placeholder, the
// chips, the reading-line pace and the loading and error lines. Copy, not
// business logic, so PA can edit it in one place. The quiet line templates
// live beside the code that fills them (briefing-model.ts, QUIET_LINE_COPY).
//
// Plain UK English, no em-dashes.

export const ASK_PLACEHOLDER = 'Ask me anything, or tell me what to do...'

export type ChipId = 'needs' | 'pipeline' | 'today'

/** The three chips inside the ask box. Each is the instruction someone would type. */
export const CHIPS: ReadonlyArray<{ id: ChipId; label: string }> = [
  { id: 'needs', label: 'What needs my attention?' },
  { id: 'pipeline', label: 'Show me the pipeline' },
  { id: 'today', label: 'Show me my procurement dashboard' },
]

/** How long the reading lines stay on screen before the answer (milliseconds). */
export const AI_THINKING_MS = 2500

export const REFRESH_FAILED_LINE = "Couldn't refresh just now. Retrying."
export const ANSWER_FAILED_LINE =
  "I couldn't answer that just now. Try one of these instead."
export const SUPPLY_HANDOFF_LINE =
  "That one is for Sophie. I've opened the Supply Chain Desk with your question."
export const APPROVE_HANDOFF_LINE =
  'The decision is yours to make on the record. I have opened it for you.'

export const SIGN_IN_TITLE = 'Sign in to your Command Center'
export const SIGN_IN_BODY = 'Sign in to see what needs your attention this morning.'

export const TODAY_HEADING = 'Today'
export const TABLE_HEADING = 'What needs your attention, and what is next'
export const SUPPLY_PENDING_LINE = "Sophie's figures will appear here shortly."
export const SEE_ALL_APPROVALS = 'See all in My Approvals'
export const BACK_TO_BRIEFING = 'Back to the briefing'

// ---------------------------------------------------------------------------
// Sentences the briefing builds from live figures (plan 05 sections 5.4, 5.5)
// ---------------------------------------------------------------------------
// The wording is fixed here and the figures are filled in by briefing-model.ts.
// A clause with no fact behind it is dropped; nothing here reads "0".

/** What a decision is called in a table row and a sentence, six stages. Gate ids never print. */
export const STEP_WORDS: Record<string, string> = {
  GD: 'baseline and scope',
  GR: 'requirements and standards',
  G1: 'route approval',
  G2: 'clarification approval',
  G3: 'scores and preferred bidder',
  G4: 'agreed position and award',
  G5: 'contractual action',
  contract_action: 'contractual action',
}

/** The five-stage names, kept beside the six-stage ones. */
export const STEP_WORDS_FIVE: Record<string, string> = {
  G1: 'route approval',
  G2: 'clarification approval',
  G3: 'evaluation lock',
  G4: 'award approval',
  G5: 'post-award review',
}

/**
 * "What needs me?": one sentence per waiting decision, keyed by the gate (or
 * `contract_action`). `{context}` becomes ": 5 findings and 1 policy check to
 * look at" and `{wait}` becomes ", waiting 2 h"; each is dropped when its fact
 * is missing.
 */
export const NEEDS_COPY: Record<string, string> = {
  GD: '{coworker} needs your decision to confirm the baseline and scope for {item}{context}{wait}.',
  GR: '{coworker} needs your approval of the requirements and standards for {item}{context}{wait}.',
  G1: '{coworker} needs your route approval for {item}{context}{wait}.',
  G2: '{coworker} needs your approval of the clarification answers for {item}{context}{wait}.',
  G3: '{coworker} needs your approval of the scores and preferred bidder for {item}{context}{wait}.',
  G4: '{coworker} needs Commercial to approve the position and award for {item}{context}{wait}.',
  G5: '{coworker} needs your post-award review for {item}{context}{wait}.',
  contract_action: '{coworker} needs your decision to choose a contractual action on the {item} contract{context}{wait}.',
  fallback: '{coworker} needs your decision on {step} for {item}{context}{wait}.',
}

/** The five-stage sentence: every decision reads the same way. */
export const NEEDS_COPY_FIVE: Record<string, string> = {
  fallback: '{coworker} needs your decision on {step} for {item}{wait}.',
}

/** The clauses of "How are we doing today?" that read a measure or a figure (plan 05 5.5). */
export const CLAIM_COPY = {
  stages: '{n} procurements are live across {stages} stages.',
  specification: '{name} drafted the FM specification in {actual}, against {byHand}.',
  invoices: '{name} found {count} invoice {noun} on the Soft FM contract this quarter, {amount}',
  kpis: '{met} of {total} KPIs were met in {month}',
  kpisAlone: '{met} of {total} KPIs were met in {month} on the Soft FM contract.',
  hours: 'Your coworkers have given back {hours} staff hours this year, {baseline}.',
  baselineFallback: 'against an agreed baseline',
  readingMeasures: 'Reading the agreed baselines and measures',
} as const

/** The action buttons under a rehearsed answer. */
export const ANSWER_ACTION_COPY = {
  /** "Open Grace's comparison". */
  comparison: "Open {name}'s comparison",
  record: 'Open the record',
} as const

/** The reading line under the ask box while a rehearsed question is matched. */
export const READING_QUESTION_LINE = 'Reading your question'

// ---- Plan 02: Megan, the three cards and the bell -----------------------------

/** Megan's answer when nothing rehearsed fits (plan 02 section 9.5). */
export const NO_ANSWER_LINE = "I don't have an answer for that one. Try one of these."
export const NOT_LOADED_LINE = 'Nothing is loaded for the demo yet.'
export const MEGAN_KICKER = 'MEGAN · PROCUREMENT ORCHESTRATOR'
/** The second line under Megan's name on the briefing (plan 11 A.1, F3). */
export const MEGAN_TITLE = 'Procurement orchestrator'
/** No longer rendered (plan 11 A.1.8 drops the kicker); kept for older imports. */
export const COWORKERS_TODAY_KICKER = 'Coworkers today'
/** The fixed status line of a working card (plan 11 A.1.2). */
export const WORKING_CARD_LINE = 'Working. Nothing is needed from you yet.'
/** The link beside the coworkers sentence (plan 11 A.1.8). */
export const MEET_THE_TEAM_LABEL = 'Meet the team'
export const MEET_THE_TEAM_HREF = '/agent-hub'
export const OPENING_EXPLORER_LINE = 'Opening the Tender Explorer...'
export const MEGAN_CAPTION = 'Megan'
/** "Show me the pipeline" opens the Explorer this long after the answer lands. */
export const PIPELINE_NAV_DELAY_MS = 1500
/** "Approve it" opens the first waiting card this long after Megan's line. */
export const APPROVE_NAV_DELAY_MS = 900
/** How long a card glows after its state changes. */
export const CARD_GLOW_MS = 5000
/** The window event the briefing raises when a card changes; the bell re-reads on it. */
export const BRIEFING_CHANGED_EVENT = 'briefing-changed'
